import React, { useEffect } from 'react';
import { Card } from '@iwoca/orion';
import { Trans } from '@lingui/react/macro';
import clsx from 'clsx';
import { useAmplitude } from 'hooks/useAmplitude';
import { TOfferDataRequirement } from 'lendingApi/lapiOverrides.types';
import _ from 'lodash';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import ukTradingHistoryStyles from '../../TradingHistory.module.css';
import { ShareRequirements } from '../ShareRequirements/ShareRequirements';
import { ArticlesOfAssociation } from './ArticlesOfAssociation/ArticlesOfAssociation';
import { BankStatements } from './BankStatements/BankStatements';
import { Bwa } from './Bwa/Bwa';
import { BwaSusa } from './BwaSusa/BwaSusa';
import { BwaSusaAnnual } from './BwaSusaAnnual/BwaSusaAnnual';
import styles from './DocumentRequirements.module.css';
import { ShareRegister } from './ShareRegister/ShareRegister';
import { TradeCertificate } from './TradeCertificate/TradeCertificate';

type TProps = {
  documentRequirements: TOfferDataRequirement['actions'];
  isSubmitted: boolean;
};

export const DocumentRequirements = ({
  documentRequirements,
  isSubmitted,
}: TProps) => {
  const { trackEvent } = useAmplitude();

  const optionalDocumentRequirements = documentRequirements.filter(
    (document) => document.is_optional,
  );

  const requiredDocumentRequirements = documentRequirements.filter(
    (document) => !document.is_optional,
  );

  useEffect(() => {
    trackEvent(AMPLITUDE_EVENTS.documentRequirementsLoaded, {
      'Optional documents':
        optionalDocumentRequirements
          ?.map((doc) => _.startCase(doc.type))
          .join(', ') || null,
      'Required documents':
        requiredDocumentRequirements
          ?.map((doc) => _.startCase(doc.type))
          .join(', ') || null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderDocumentRequirements = (
    requirements: TOfferDataRequirement['actions'],
  ) => {
    const DOCUMENT_REQUIREMENTS: { [key: string]: React.ElementType } = {
      kontoauszuege_hochladen: BankStatements,
      bwa_hochladen: Bwa,
      bwa_susa_hochladen: BwaSusa,
      jaehrliche_bwa_susa_hochladen: BwaSusaAnnual,
      gewerbeschein_hochladen: TradeCertificate,
      gesellschaftsvertrag_hochladen: ArticlesOfAssociation,
      aktienregisterauszug_hochladen: ShareRegister,
    };

    return requirements?.map((requirement) => {
      if (!requirement.type || !DOCUMENT_REQUIREMENTS[requirement.type]) {
        return null;
      }

      const RequirementDetails = DOCUMENT_REQUIREMENTS[requirement.type];

      return (
        <RequirementDetails
          key={requirement.type}
          isOptional={requirement.is_optional}
          quantifier={requirement.quantifier?.value}
          resolutionMethods={requirement.resolution_methods}
          status={requirement.status}
          isSubmitted={isSubmitted}
        />
      );
    });
  };

  return (
    <>
      <Card className={ukTradingHistoryStyles.cardContainer}>
        <div className={styles.wrapper}>
          <div>{renderDocumentRequirements(requiredDocumentRequirements)}</div>
          {optionalDocumentRequirements.length > 0 && (
            <div className={styles.optionalDocsWrapper}>
              <h4 className={ukTradingHistoryStyles.title}>
                <Trans>Optional documents</Trans>
              </h4>
              {renderDocumentRequirements(optionalDocumentRequirements)}
            </div>
          )}
        </div>
      </Card>
      <Card
        className={clsx(
          ukTradingHistoryStyles.cardContainer,
          ukTradingHistoryStyles.shareReqCard,
        )}
      >
        <ShareRequirements
          requiredDocs={requiredDocumentRequirements}
          optionalDocs={optionalDocumentRequirements}
        />
      </Card>
    </>
  );
};
